// External libraries
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';

// Own libraries
import { ListItem } from '@eventbrite/eds-list-item';
import { STYLE_BASIC } from '@eventbrite/eds-input-field';
import { DisplayAmount } from '@eventbrite/eds-read-more';

// Local imports
import SelectTicketVariant from './reserved/SelectTicketVariant';
import { TierColorCircle } from '../../components/tierColorCircle/TierColorCircle';
import { SMALL_COLOR_CIRCLE_SIZE } from '../../components/tierColorCircle/constants';
import {
    TIERED_TICKET_STATUS_PLACEMENT_REPLACE_QUANTITY,
    TIERED_TICKET_STATUS_PLACEMENT_DESCRIPTION,
} from './utils/ticketTierUtilsConstants';
import { PROMO_CODE_OPTIONS_PROP_TYPE } from '../../components/promoCode/constants';
import { getVariantCostForDisplay } from './utils/ticketPriceUtils';
import { determineTieredOnsaleStatusDisplayOptions } from './utils/ticketTierUtils';
import './TieredTicketDisplayCardContent.scss';
import {
    isNotAddOnTicketWithVariants,
    isAddOnTicketWithVariants,
    isAdmissionTicket,
} from './utils/ticketUtils';
import { gettext } from '@eventbrite/i18n';
import TicketQuantitySelector from '../../components/ticketsSelectionList/TicketQuantitySelector';
import TicketPaymentConstraints from '../../components/ticketsSelectionList/TicketPaymentConstraints';
import TicketDescription from '../../components/ticketsSelectionList/TicketDescription';
import FeeAndTaxContent from './FeeAndTaxContent';

const TicketDescriptionContent = ({
    statusHtml,
    description,
    descriptionClassNames,
    hasStatus,
    hasDescription,
}) => {
    if (!hasStatus && !hasDescription) {
        return null;
    }

    let statusContent = null;
    const statusClassNames = classNames({
        'eds-l-mar-bot-2': hasDescription,
    });

    if (hasStatus) {
        statusContent = (
            <p
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={statusHtml}
                data-spec="tiered-ticket-onsale-status-description"
            />
        );
    }

    return (
        <div className={descriptionClassNames} data-spec="description-content">
            <div className={statusClassNames}>{statusContent}</div>
            {hasDescription && (
                <TicketDescription collapsedDisplayAmount={DisplayAmount.small}>
                    {description}
                </TicketDescription>
            )}
        </div>
    );
};

const SingleTierDisplayContent = ({ ticket, isEventWithShippingFee }) => {
    const variant = ticket.variants[0];
    const priceDisplayText = getVariantCostForDisplay(
        variant,
        ticket.currencyFormat,
    );
    let colorCircle = null;

    if (isAdmissionTicket(ticket)) {
        colorCircle = (
            <TierColorCircle
                tierColor={variant.color}
                colorCircleSize={SMALL_COLOR_CIRCLE_SIZE}
            />
        );
    }

    const priceDisplayTextContent = priceDisplayText ? (
        <span className="eds-l-pad-right-1">{priceDisplayText}</span>
    ) : null;

    const feeAndTaxContent = isEventWithShippingFee ? (
        <FeeAndTaxContent feesAndTaxes={variant.feeAndTax}></FeeAndTaxContent>
    ) : null;

    return (
        <div className="single-tier-content" data-spec="single-tier-content">
            <ListItem extraContent={colorCircle}>
                <p
                    className="eds-text-bm eds-text-color--grey-600 eds-text-color--grey-600"
                    data-spec="single-tier-price"
                >
                    {priceDisplayTextContent}
                    {feeAndTaxContent}
                </p>
            </ListItem>
        </div>
    );
};

const closest = (range, value) =>
    range.reduce(
        (prev, curr) =>
            Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
        0,
    );

const getNextQuantity = (value, range) => {
    if (range.includes(value)) {
        return value;
    }
    return closest(range, value);
};

export default class TieredTicketDisplayCardContent extends PureComponent {
    static propTypes = {
        handleTicketQuantityChange: PropTypes.func.isRequired,
        handleTicketVariantChange: PropTypes.func,
        ticket: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            description: PropTypes.string,
            variants: PropTypes.array,
            currencyFormat: PropTypes.string,
        }),
        promoCodeFormOptions: PROMO_CODE_OPTIONS_PROP_TYPE,
        isFormSubmitting: PropTypes.bool,
    };

    static defaultProps = {
        statusDisplayOptions: {},
    };

    state = {
        selectedVariantId: null,
        selectedQuantity: 0,
    };

    componentDidMount() {
        this.setDefaultVariant(this.props.ticket);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!this.hasSelectedVariant(nextProps)) {
            this.setDefaultVariant(nextProps.ticket);
        } else if (this.selectedQuantityHasChanged(nextProps)) {
            this._updateTicketSelectedQuantity(nextProps);
        }
    }

    setDefaultVariant = (ticket) => {
        /**
         * UX consideration: when a ticket is an Add-on with nested variants, the quantity selector
         * is disabled until a variant is chosen. In other words, there is no default quantity
         * selection -- even zero.
         */
        if (isAddOnTicketWithVariants(ticket)) {
            return;
        }

        const [firstVariant] = ticket.variants;

        if (firstVariant) {
            const { id, ticketOptionsRange } = firstVariant;
            let selectedVariantId;
            let selectedQuantity;

            // a previously selected variant may exist if the page was reloaded after
            // a new language was selected
            const previouslySelectedVariant = ticket.variants.find(
                (variant) => variant.selectedQuantity > 0,
            );

            if (previouslySelectedVariant) {
                selectedVariantId = previouslySelectedVariant.id;
                selectedQuantity = previouslySelectedVariant.selectedQuantity;
            } else {
                selectedVariantId = id;
                selectedQuantity = ticketOptionsRange[0];
            }

            this.setState({
                selectedVariantId,
                selectedQuantity,
            });
        }
    };

    handleTicketVariantChange = (nextVariant) => {
        const handleTicketVariantChange = this.props.handleTicketVariantChange;

        /**
         * Affordance that returns the closest quantity in the ticket options range of the next
         * selected ticket if the current selected quantity is not availabile in the next ticket's
         * options range.
         */
        const nextQuantity = getNextQuantity(
            this.state.selectedQuantity,
            nextVariant.ticketOptionsRange,
        );

        this.setState({
            selectedVariantId: nextVariant.id,
            selectedQuantity: nextQuantity,
        });

        handleTicketVariantChange({
            id: this.props.ticket.id,
            variantId: nextVariant.id,
            quantity: nextQuantity,
        });
    };

    _handleTicketQuantityChange = (quantity) => {
        const {
            ticket: { id },
            handleTicketQuantityChange,
        } = this.props;
        const { selectedVariantId } = this.state;
        const quantityNumber = Number(quantity);

        this.setState({
            selectedQuantity: quantityNumber,
        });

        handleTicketQuantityChange({
            id,
            variantId: selectedVariantId,
            quantity: quantityNumber,
        });
    };

    selectedQuantityHasChanged = (props) => {
        const { selectedQuantity: nextQuantity } =
            this.getSelectedVariant(props);
        const { selectedQuantity: quantity } = this.state;

        return nextQuantity !== quantity;
    };

    _updateTicketSelectedQuantity = (props) => {
        const { selectedQuantity } = this.getSelectedVariant(props);

        this.setState({ selectedQuantity });
    };

    hasSelectedVariant = (props) => !!this.getSelectedVariant(props);

    getSelectedVariant = (props) => {
        const selectedVariantId = this.state.selectedVariantId;
        const ticket = props.ticket;
        const variants = ticket.variants;

        if (selectedVariantId) {
            return variants.find((variant) => variant.id === selectedVariantId);
        }

        // Returns the first variant of a Ticket Rule which is the primary variant that is used
        // for the Best Available option.
        if (isNotAddOnTicketWithVariants(ticket)) {
            return variants[0];
        }

        // Return null for add-ons with nested variants
        return null;
    };

    render() {
        const { ticket, locale, isFormSubmitting, isEventWithShippingFee } =
            this.props;
        const { id, name, variants, currencyFormat, promoCodeFormOptions } =
            ticket;
        const selectedVariant = this.getSelectedVariant(this.props);
        const selectedVariantTicketOptionsRange = get(
            selectedVariant,
            'ticketOptionsRange',
            [0],
        );

        const selectedQuantity = this.state.selectedQuantity ?? 0;

        // Excludes add-on tiers from on sale property calcuations
        const ticketClassToRender = isAddOnTicketWithVariants(ticket)
            ? selectedVariant
            : ticket;
        let statusHtml;
        let placement;
        let hideDescription;
        let isDisabled;
        let description;

        if (ticketClassToRender) {
            ({ statusHtml, placement, hideDescription, isDisabled } =
                determineTieredOnsaleStatusDisplayOptions(
                    ticketClassToRender,
                    locale,
                ));
            description = get(ticketClassToRender, 'description', null);
        }

        const shouldDisableQuantitySelector =
            selectedVariant === null ||
            (ticket.isDisabled && ticket.conflictingPayment) ||
            isFormSubmitting;
        const hasStatus =
            statusHtml &&
            placement === TIERED_TICKET_STATUS_PLACEMENT_DESCRIPTION;
        const hasDescription = description && !hideDescription;
        const hasReplacementText =
            placement === TIERED_TICKET_STATUS_PLACEMENT_REPLACE_QUANTITY;
        const variantDisplayBox =
            variants.length === 1 ? (
                <SingleTierDisplayContent
                    ticket={ticket}
                    isEventWithShippingFee={isEventWithShippingFee}
                />
            ) : (
                <SelectTicketVariant
                    variants={variants}
                    handleChangeVariant={this.handleTicketVariantChange}
                    selectedVariant={selectedVariant}
                    currencyFormat={currencyFormat}
                    promoCodeFormOptions={promoCodeFormOptions}
                    locale={locale}
                    isFormSubmitting={isFormSubmitting}
                    isEventWithShippingFee={isEventWithShippingFee}
                />
            );
        const rightContent = hasReplacementText ? (
            <div className="eds-text-color--grey-600 eds-text--right">
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={statusHtml}
                    data-spec="tiered-ticket-onsale-status-label"
                    className="tiered-ticket-onsale-status__label"
                />
            </div>
        ) : (
            <TicketQuantitySelector
                id={id}
                label={gettext('Select quantity: %(name)s', { name })}
                name={name}
                ticketOptionsRange={selectedVariantTicketOptionsRange}
                selectedQuantity={selectedQuantity}
                onQuantityChange={this._handleTicketQuantityChange}
                style={STYLE_BASIC}
                value={selectedQuantity}
                isDisabled={shouldDisableQuantitySelector}
            />
        );
        const rootClassNames = classNames(
            'tiered-ticket-display-content-root',
            'eds-g-cell',
            'eds-g-cell-1-1',
            'eds-l-pad-top-6 eds-l-pad-bot-2',
            {
                'eds-l-pad-bot-6':
                    variants.length === 1 ||
                    (hasStatus && !hasReplacementText) ||
                    hasDescription,
            },
        );
        const contentClassNames = classNames(
            'eds-ticket-card-content',
            'eds-ticket-display-card-content',
            'tiered-ticket-display-content',
            { 'tiered-ticket-display-content--disabled': isDisabled },
        );
        const headerClassNames = classNames(
            'eds-g-cell',
            'eds-g-cell-9-12',
            'eds-text-bl--fixed',
            'eds-text-color--grey-900',
            {
                'eds-l-mar-bot-4': variants.length === 1,
                'eds-l-mar-bot-2': variants.length > 1,
            },
        );
        const descriptionClassNames = classNames(
            'eds-text-color--grey-600 eds-text-bm',
            {
                'eds-l-pad-top-4': variants.length === 1,
            },
        );

        let paymentConstraintsDisplay = null;

        if (!isEmpty(ticket.paymentConstraints)) {
            const usedPaymentMethods = {};
            const uniquePaymentConstraints = ticket.paymentConstraints.filter(
                ({ paymentMethod }) => {
                    if (!usedPaymentMethods[paymentMethod]) {
                        usedPaymentMethods[paymentMethod] = true;
                        return true;
                    }

                    return false;
                },
            );

            paymentConstraintsDisplay = (
                <TicketPaymentConstraints
                    paymentConstraints={uniquePaymentConstraints}
                />
            );
        }

        return (
            <div
                className={rootClassNames}
                data-spec="tiered-ticket-display-card-content"
            >
                <div className={contentClassNames}>
                    <div className="eds-g-cell-1-1">
                        <h3
                            className={headerClassNames}
                            data-spec="tiered-ticket-name"
                        >
                            {name}
                        </h3>
                        {paymentConstraintsDisplay}
                        {variantDisplayBox}
                    </div>
                    {rightContent}
                </div>
                <TicketDescriptionContent
                    descriptionClassNames={descriptionClassNames}
                    statusHtml={statusHtml}
                    description={description}
                    hasDescription={hasDescription}
                    hasStatus={hasStatus}
                />
            </div>
        );
    }
}
